import type { FC, PropsWithChildren } from 'react'
import { Suspense } from 'react'
import { Outlet, useLocation, useMatches } from 'react-router-dom'
import { observer } from 'mobx-react-lite'

import { FileUploadPanel } from '@jotta/files'
import type {
  AppLayoutRouteHandle,
  AppLayoutSurface,
} from '@jotta/ui/AppLayout'
import { AppLayout } from '@jotta/ui/AppLayout'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import { useBrandStore } from '@jotta/ui/useBrandTheme'

import { Header } from './Header'
import { NavBar } from './NavBar'
import { WebappBanner } from './WebappBanner'
import { WebappModuleNav } from './WebappModuleNav'

const surfaceKey = 'appLayoutSurface' satisfies keyof AppLayoutRouteHandle

function useAppLayoutSurface(): AppLayoutSurface {
  const ownsSurface = useMatches().some(
    ({ handle }) =>
      typeof handle === 'object' &&
      handle !== null &&
      surfaceKey in handle &&
      handle[surfaceKey] ===
        ('none' satisfies AppLayoutRouteHandle[typeof surfaceKey]),
  )
  return ownsSurface ? 'none' : 'card'
}

export const PrivateLayout: FC<PropsWithChildren> = observer(
  function WebappLayout({ children = <Outlet /> }) {
    const { pathname } = useLocation()
    const brandStore = useBrandStore()
    const surface = useAppLayoutSurface()
    const overlay = brandStore.slots.overlay
    const hasOverlay = brandStore.hasOverlay
    // Photos still use UploadPhotoQueueStatus; hide the file panel there to
    // avoid stacking two uploaders until photos migrate to useUploadStore.
    const showFileUploadPanel = !pathname.startsWith('/photo')
    return (
      <AppLayout
        navbar={<NavBar />}
        banner={<WebappBanner />}
        moduleNav={<WebappModuleNav />}
        header={<Header />}
        overlay={hasOverlay ? overlay : undefined}
        surface={surface}
      >
        <Suspense fallback={<LoadingOverlay id="PrivateLayout" open />}>
          {children}
        </Suspense>
        {showFileUploadPanel && <FileUploadPanel />}
      </AppLayout>
    )
  },
)
